import { useState, useRef, useEffect } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useUserSearch } from "@/hooks/useUserSearch";
import { useNavigate } from "react-router-dom";

interface UserSearchProps {
  placeholder?: string;
  onUserSelect?: (userId: string) => void;
  excludeUserId?: string;
  className?: string;
}

export const UserSearch = ({ 
  placeholder = "Search by username...", 
  onUserSelect,
  excludeUserId,
  className = "" 
}: UserSearchProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { searchTerm, setSearchTerm, results, loading } = useUserSearch(excludeUserId);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [setSearchTerm]);

  // Show dropdown when there's a search term and results
  useEffect(() => {
    setIsOpen(searchTerm.length >= 2 && (results.length > 0 || loading));
  }, [searchTerm, results, loading]);

  const handleUserClick = (userId: string) => {
    if (onUserSelect) {
      onUserSelect(userId);
    } else {
      navigate(`/gift?user=${userId}`);
    }
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    setSearchTerm('');
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-10"
        />
        {searchTerm && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (
        <Card className="absolute top-full mt-2 w-full z-50 shadow-warm border-border/50 max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="p-4 flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Searching...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {searchTerm.length < 2 
                ? "Type at least 2 characters to search" 
                : `No users found matching "${searchTerm}"`
              }
            </div>
          ) : (
            <div className="py-2">
              {results.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleUserClick(user.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted transition-colors"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={user.avatar_url || undefined} />
                    <AvatarFallback className="bg-muted">
                      {user.name?.[0] || user.username?.[0] || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium text-sm truncate">
                      @{user.username}
                    </div>
                    {user.name && (
                      <div className="text-xs text-muted-foreground truncate">
                        {user.name}
                      </div>
                    )}
                  </div>
                  <div className={`w-2 h-2 rounded-full ${
                    user.is_active ? 'bg-green-500' : 'bg-muted-foreground/30'
                  }`} />
                </button>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};
